import { useEffect, useState } from 'react';
import axios from 'axios';
import { Trophy, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StudentResults() {
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAttempts();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('studentToken');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchAttempts = async () => {
    try {
      const response = await axios.get(`${API}/quiz-attempts/student`, getAuthHeaders());
      setAttempts(response.data);
    } catch (error) {
      console.error('Error fetching attempts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-brand-dark font-display">My Quiz Results</h1>
        <p className="text-slate-600 mt-2">Track your performance and progress</p>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-brand-blue border-r-transparent"></div>
          <p className="mt-4 text-slate-600">Loading results...</p>
        </div>
      ) : attempts.length === 0 ? (
        <div className="text-center py-12">
          <Trophy className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600 text-lg">No quiz attempts yet</p>
          <p className="text-slate-500 text-sm mt-2">Start taking quizzes to see your results here</p>
          <Link to="/student/dashboard">
            <Button className="mt-4">Go to Quizzes</Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {attempts.map((attempt) => {
            const percentage = (attempt.score / attempt.total_questions) * 100;
            const passed = percentage >= 60;

            return (
              <div
                key={attempt.attempt_id}
                data-testid={`attempt-${attempt.attempt_id}`}
                className="border border-slate-200 rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-brand-dark">{attempt.quiz_title}</h3>
                    <div className="flex items-center text-sm text-slate-500 mt-1">
                      <Calendar className="h-4 w-4 mr-1" />
                      {formatDate(attempt.timestamp)}
                    </div>
                  </div>
                  <div className={`px-4 py-2 rounded-full ${passed ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                    <span className="font-semibold">{percentage.toFixed(1)}%</span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-brand-blue">{attempt.score}</p>
                    <p className="text-sm text-slate-600">Score</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-slate-700">{attempt.total_questions}</p>
                    <p className="text-sm text-slate-600">Total Questions</p>
                  </div>
                  <div className="text-center">
                    <p className={`text-2xl font-bold ${passed ? 'text-green-600' : 'text-orange-600'}`}>
                      {passed ? 'Passed' : 'Failed'}
                    </p>
                    <p className="text-sm text-slate-600">Status</p>
                  </div>
                </div>

                <Link to={`/student/quiz-result/${attempt.attempt_id}`}>
                  <Button variant="outline" className="w-full" data-testid={`view-result-${attempt.attempt_id}`}>
                    View Detailed Results
                  </Button>
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
